/**
 * The guest's half of the seat claim. The token arrives in the QR deep link and
 * lives in localStorage from then on, so a locked or backgrounded phone rejoins
 * exactly where it was (01-ARCHITECTURE.md).
 */
const KEY = 'continuum.session';

export interface Session {
  voyageId: string;
  seat: number;
  token: string;
}

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const s = parsed as Partial<Session>;
    if (typeof s.voyageId !== 'string' || typeof s.token !== 'string') return null;
    if (typeof s.seat !== 'number') return null;
    return { voyageId: s.voyageId, seat: s.seat, token: s.token };
  } catch {
    return null;
  }
}

function write(session: Session): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // A private window without storage still boards; it just cannot rejoin silently.
  }
}

/**
 * Resolve the session from the URL the QR code landed on, falling back to whatever
 * this phone already holds. The URL wins — a fresh pass re-seats the phone.
 */
/** A link typed or mangled by hand should fall back to the saved session, not throw. */
function safeDecode(part: string): string | null {
  try {
    return decodeURIComponent(part);
  } catch {
    return null;
  }
}

export function resolveSession(
  search = window.location.search,
  path = window.location.pathname,
): Session | null {
  const params = new URLSearchParams(search);

  // A guest's own link: /s/<voyage>/<seat token>. One address per guest; the token carries
  // the seat and the expiry, and the voyage is its full name (usually just the date).
  const printed = /^\/s\/([^/]+)\/([^/]+)\/?$/.exec(path);
  const voyageName = printed ? safeDecode(printed[1] ?? '') : null;
  const passToken = printed ? safeDecode(printed[2] ?? '') : null;
  if (voyageName && passToken) {
    const passSeat = Number(passToken.split('.')[0] ?? '0');
    if (Number.isInteger(passSeat) && passSeat > 0) {
      const session = { voyageId: `voyage:${voyageName}`, seat: passSeat, token: passToken };
      write(session);
      // Straight off the address bar, before there is anything on screen to photograph.
      window.history.replaceState({}, '', '/');
      return session;
    }
  }

  const token = params.get('t');
  const seat = Number(params.get('seat') ?? '0');
  const voyageId = params.get('voyage');

  if (token && voyageId && Number.isInteger(seat) && seat > 0) {
    const session = { voyageId, seat, token };
    write(session);
    // Keep the token out of the address bar and out of any screenshot.
    window.history.replaceState({}, '', window.location.pathname);
    return session;
  }
  return read();
}

/** The steward moved this guest to another seat: the new pass becomes this phone's own. */
export function saveSession(session: Session): void {
  write(session);
}

/**
 * What this phone has done under its seat comes with it to the new one (Marek, 2026-10-24: a guest moved keeps
 * their progress): the role card seen, its ballots, the hidden signal's call and reminder. Kept under keys with the
 * seat in them (App.tsx, screens/Signal.tsx `signalCallKey`), so each is copied to the new seat's own key.
 */
export function carrySeatKeys(voyageId: string, from: number, to: number): void {
  if (from === to) return;
  try {
    const prefixes = [
      `continuum.role.${voyageId}.`,
      `continuum.ballots.${voyageId}.`,
      `continuum.signal.call.${voyageId}.`,
      `continuum.signal.reminder.${voyageId}.`,
      `continuum.catchup.${voyageId}.`,
    ];
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) keys.push(key);
    }
    for (const key of keys) {
      for (const prefix of prefixes) {
        if (!key.startsWith(`${prefix}${from}.`)) continue;
        const value = localStorage.getItem(key);
        if (value !== null) localStorage.setItem(`${prefix}${to}.${key.slice(`${prefix}${from}.`.length)}`, value);
      }
    }
  } catch {
    /* private browsing: the guest may see a card again */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Language choice survives a reload; it is not worth a round trip. */
export function readLang(): 'en' | 'no' | null {
  try {
    const v = localStorage.getItem('continuum.lang');
    return v === 'en' || v === 'no' ? v : null;
  } catch {
    return null;
  }
}

export function writeLang(lang: 'en' | 'no'): void {
  try {
    localStorage.setItem('continuum.lang', lang);
  } catch {
    /* ignore */
  }
}
